import {
  CanCreateOfflinePo,
  CanEditOfflineSupplierTransmission,
  CanManageOfflineSuppliers
} from 'src/constants/permissionsList';
import { getSupplierEditAccess, SupplierEditAccessInput } from 'src/sections/supplier/utils/supplierEditAccess';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';

/** Permission 684 — full manage suppliers access. */
export const SupplierManagePermissions = CanManageOfflineSuppliers;

/** Permission 820 — transmission settings only. */
export const SupplierTransmissionPermissions = CanEditOfflineSupplierTransmission;

/** Permission 680 — create PO / view suppliers page access. */
export const SupplierPageAccessPermissions = CanCreateOfflinePo;

/** Grid Supplier Info column: Edit when 684 or 820 is enabled. */
export const SupplierInfoEditPermissions = [
  ...SupplierManagePermissions,
  ...SupplierTransmissionPermissions,
];

/** Requires 680 (page access) AND 684 (manage suppliers). */
export const SupplierWith680And684Permissions = [
  ...SupplierPageAccessPermissions,
  ...SupplierManagePermissions,
];

/** Requires 680 (page access) AND 820 (transmission settings). */
export const SupplierWith680And820Permissions = [
  ...SupplierPageAccessPermissions,
  ...SupplierTransmissionPermissions,
];

/** Page entry: 680, 684, or 820. */
export const SupplierListPagePermissions = [
  ...SupplierPageAccessPermissions,
  ...SupplierManagePermissions,
  ...SupplierTransmissionPermissions,
];

/**
 * Edit button in view-until-edit supplier panels.
 * Online catalog overrides require 680+684; offline allows 680+684 or 680+820.
 */
export const canShowSupplierInfoEditButton = (
  isOnlineCatalogEdit: boolean,
  has680And684: boolean,
  has680And820: boolean
): boolean => {
  if (isOnlineCatalogEdit) {
    return has680And684;
  }

  return has680And684 || has680And820;
};


export const toSupplierEditAccessContext = (
  supplier: Pick<ManageSupplier, 'supplierType' | 'status' | 'isSharedOfflineSupplier'>
): SupplierEditAccessInput => ({
  supplierType: supplier.supplierType,
  status: supplier.status,
  isSameCompany: !supplier.isSharedOfflineSupplier,
});

export const isOnlineManageSupplier = (
  supplier: Pick<ManageSupplier, 'supplierType' | 'isOfflineSupplier'>
): boolean => !supplier.isOfflineSupplier || supplier.supplierType !== 'offline';

/** Grid Supplier Info column label: Edit when the user can edit that supplier row. */
export const canEditSupplierInGrid = (
  supplier: Pick<
    ManageSupplier,
    'supplierType' | 'status' | 'isSharedOfflineSupplier' | 'isOfflineSupplier'
  >,
  canManageSuppliers: boolean,
  canManageTransmissionSettings: boolean
): boolean => {
  const hasTransmissionOnlyAccess =
    canManageTransmissionSettings && !canManageSuppliers;

  if (hasTransmissionOnlyAccess && isOnlineManageSupplier(supplier)) {
    return false;
  }

  const hasEditPermission = canManageSuppliers || canManageTransmissionSettings;

  const access = getSupplierEditAccess({
    supplier: toSupplierEditAccessContext(supplier),
    canManageSuppliers,
    canManageTransmissionSettings,
    hasEditPermission,
  });

  return access.mode !== 'view';
};
